import EanCode, { OracleEanCode } from "./EanCode";
import ArticleTraitValue from "../ArticleTraits/ArticleTraitValue";
import { MediaUrl } from "../../redux/api/menuDataApi";
import { ItemDiscount } from "../../redux/api/simphonyApi.ts";
import { ArrangementGroup } from "../../../../types/qr/arrangements.ts";
import { OpeningHours } from "@jamezz/react-components";
import { Language } from "../../../../types/shared/language.ts";

export enum ArticleType {
  Regular,
  // Not a product, just a text message appearing in your catalogus
  InfoDismissable,
  // Unorderable product acting as a spacer in your catalogus
  Filler,
  // Show article as service request in app
  ServiceRequest,

  Deposit,
}

export enum ArticleOrderTimeState {
  Disabled,
  Hidden,
}

export interface PriceKey {
  discountPercentage?: number;
  type?: string;
  price?: number;
}

/**
 * Not always certain what backoffice will return, so add undefined as optional type
 */
export type ArticeTranslationKeys = "naam" | "detailed_description" | "omschrijving" | string;
export type ItemTranslations<T> = PartialRecord<Language, PartialRecord<T, string | undefined>>;
export type ArticleTranslations = ItemTranslations<ArticeTranslationKeys>;

export type ArrangementGroupProduct = {
  id: number;
  arrangement_group_id: number;
  product_id: number;
  credit_cost?: ArrangementGroup["credit_cost"];
};

export default interface Article {
  id: string;
  apiId1: string;
  apiId2: string | null;
  name: string;
  hashedName: string;
  printName: string | null;
  imageMediaUrls: MediaUrl[];
  mealConfiguratorStart?: MediaUrl;
  mealConfiguratorLayer?: MediaUrl;
  mealConfiguratorLayers: MediaUrl[];
  mealConfiguratorSteps?: { steps: { imageIndex: number; layerIndex: number | null }[] };
  originalPrice: number;
  price: number; // price = original price - discounts
  vaprice: number; // price = original price - discounts
  discounts: PartialRecord<string, { discount: number }>;
  propposId: number | string | null;
  blocked: boolean;
  description: string;
  optionGroupIds: string[];
  canOrder: boolean;
  stock: number;
  isOutOfStock?: boolean;
  type: ArticleType;
  openingHours?: OpeningHours;
  menuTagIds: string[];
  sortKey: number;
  eanCodes?: (EanCode | OracleEanCode)[];
  addExtraArticleIds?: { articleId: string; type: ArticleType }[];
  priceKeys?: PartialRecord<string, PriceKey>;
  minCount?: number;
  maxCount: number | null;
  upsellMenu?: string;
  translations?: ArticleTranslations;
  apiData: any;
  articleTraits?: PartialRecord<string, ArticleTraitValue>;
  customData: {
    meal_configurator?: { layer_index?: number };
    local_price?: number;
    oracleDiscount?: ItemDiscount;
    box_tooltip?: { custom_html?: string };
    bgColor?: string;
  } | null;
  requireAge?: number;
  /// Specific for option items
  defaultCount?: number;
  score?: number;
  isTip: boolean;
  updatedAt: string;
  hasCustomizedPriceAndName?: boolean;
  optionGroupOverwrites?: { [optionGroupId: string]: ProductOptionGroupPivot };
  optionGroupProductOverwrites?: ProductOptionGroupProductPivot;
  isFoodArticle?: boolean;
  isDrinksArticle?: boolean;
  isNotAvailable: boolean;
  sendToApi: boolean;
  productPriceLines: PartialRecord<number, number>;
  arrangementGroupProducts?: PartialRecord<number, ArrangementGroupProduct>;
}

export type ProductOptionGroupProductPivot = {
  price?: number;
};

export type ProductOptionGroupPivot = {
  sortKey?: number | null;
  minCount?: number | null;
  maxCount?: number | null;
  productPrices?: { [key: string]: number } | null;
};

export type ProductOptionGroupPivotApi = ProductOptionGroupPivot & {
  option_group_id: number;
  product_id: number;
  id: number;
  vestiging_id?: number;
};

export function getArticlePrice(
  article: Article,
  options: { salesAreaPriceLineId: number | null } | undefined = undefined
): number {
  if (article.price !== article.originalPrice) {
    return article.price;
  }
  const productPrice = article.productPriceLines[options?.salesAreaPriceLineId ?? 0];
  if (productPrice != null) {
    return productPrice / 100.0;
  }
  return article.price;
}

export function calculateArticlePrice(article: Article) {
  let totalDiscount = 0;
  Object.keys(article.discounts).forEach((discountKey) => {
    if (article.discounts[discountKey]) {
      totalDiscount += article.discounts[discountKey].discount;
    }
  });
  return article.originalPrice - totalDiscount;
}

export function getArticleName(article: Article, language: Language) {
  if (article.translations?.[language]?.naam) {
    return article.translations?.[language]?.naam;
  }

  return article.name;
}

export function getArticleDescription(article: Article, language: Language, tryDetailedDescription: boolean = false) {
  if (tryDetailedDescription && article.translations?.[language]?.detailed_description) {
    return article.translations?.[language]?.detailed_description;
  }

  if (article.translations?.[language]?.omschrijving) {
    return article.translations?.[language]?.omschrijving;
  }

  return article.description;
}
